import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { needsGuestContact, validateGuestContact, type GuestContactErrors } from '../../utils/guestContact';
import { BookingStepProgress } from './BookingStepProgress';
import './BookingComponents.css';
import './booking-brand.css';

export interface CartLineVM {
  serviceId: string;
  serviceName?: string;
  price?: number;
  currency?: string | null;
  durationMinutes?: number;
  slotStartTime?: string | null;
  resourceId?: string | null;
  resourceName?: string | null;
  /** Esta línea (con cobro) podría pagarse con un bono/crédito del usuario (preview no-consumidor). */
  bonoEligible?: boolean;
  bonoSource?: 'bundle' | 'credits' | null;
}

/** Resumen del bono/crédito disponible para el carrito (preview no-consumidor, server-side). */
export interface CartBonoVM {
  available: boolean;
  bundleSessionsRemaining: number;
  creditsBalance: number;
  coverableCount: number;
}

export interface CartSummaryVM {
  items: CartLineVM[];
  totalAmount: number; // céntimos (server-side)
  totalDurationMinutes?: number;
  currency?: string | null;
  paymentMode?: 'prepaid' | 'postpaid' | 'free';
  /** Modalidades que el carrito permite elegir (regla 2, calculado server-side). Vacío = gratis. */
  allowedPaymentModes?: ('prepaid' | 'postpaid')[];
  /** El negocio aún no puede cobrar online (Stripe sin conectar): se paga en el centro y se avisa. */
  onlinePaymentUnavailable?: boolean;
  bono?: CartBonoVM | null;
  count?: number;
}

interface CartReviewProps {
  cart: CartSummaryVM;
  language?: string;
  /** true si el usuario ya tiene identidad (Clerk/end_user); oculta el formulario de contacto. */
  hasIdentity?: boolean;
  onRemoveLine: (index: number) => void;
  onAddAnother: () => void;
  onConfirm: (
    contact?: { email?: string; name?: string; phone?: string },
    paymentMode?: 'prepaid' | 'postpaid',
    bonoLineIndexes?: number[],
  ) => void;
  /** Error devuelto por el servidor al confirmar (p. ej. faltan datos): se muestra sin perder el carrito. */
  serverError?: string | null;
  /** Lo que ya sabemos del cliente (p. ej. su ficha de Clerk) para no hacerle reescribirlo. */
  initialContact?: { name?: string; email?: string; phone?: string };
  /** Reeditar una línea: re-ejecuta el sub-flujo de ese servicio (fecha/hora/profesional). */
  onEditLine?: (index: number, line: CartLineVM) => void;
  /** Abre la pantalla de compra de packs desde la opción de pago "con un pack". */
  onBuyBundle?: () => void;
}

const L: Record<string, Record<string, string>> = {
  es: {
    step: 'Paso 3 de 3', title: 'Revisa y confirma', empty: 'Aún no has añadido servicios', total: 'Total',
    remove: 'Quitar', edit: 'Cambiar hora', addAnother: 'Añadir otro servicio',
    howToPay: 'Cómo quieres pagar', name: 'Nombre', email: 'Email', phone: 'Teléfono', free: 'Gratuito',
    contact: 'Tus datos de contacto',
    contactHint: 'Déjanos un email o un teléfono para enviarte la confirmación.',
    nameRequired: 'Escribe tu nombre.', contactRequired: 'Indica un email o un teléfono.',
    emailInvalid: 'Ese email no parece válido.', phoneInvalid: 'Ese teléfono no parece válido.',
    onlinePayUnavailable: 'El pago online aún no está disponible en este negocio: pagarás en el centro.',
    min: 'min', h: 'h',
    payNowTitle: 'Pagar ahora, con tarjeta', payNowHint: 'Plaza garantizada al instante',
    payLaterTitle: 'En el centro', payLaterHint: 'Se libera si no llegas 10 min antes',
    bonoTitle: 'Con un pack de sesiones', bonoNone: 'No tienes packs activos',
    bonoBuy: 'Comprar', bono: 'Pack',
    confirmFree: 'Confirmar reserva', payAndBook: 'Pagar {amount} y reservar',
    bookAndPayLater: 'Reservar y pagar en el centro',
    // Tinoo NO emite facturas (decisión legal de sep-2026: las emite la cuenta Stripe del
    // negocio). Prometerla aquí es prometer algo que no llega.
    footnote: 'Recibirás la confirmación por email.',
    sessionsLeft: 'sesiones disponibles', creditsLeft: 'créditos', with: 'con',
  },
  en: {
    step: 'Step 3 of 3', title: 'Review and confirm', empty: 'No services added yet', total: 'Total',
    remove: 'Remove', edit: 'Change time', addAnother: 'Add another service',
    howToPay: 'How would you like to pay', name: 'Name', email: 'Email', phone: 'Phone', free: 'Free',
    contact: 'Your contact details',
    contactHint: 'Leave an email or a phone number so we can send you the confirmation.',
    nameRequired: 'Enter your name.', contactRequired: 'Enter an email or a phone number.',
    emailInvalid: 'That email does not look valid.', phoneInvalid: 'That phone number does not look valid.',
    onlinePayUnavailable: 'Online payment is not available at this business yet: you will pay at the venue.',
    min: 'min', h: 'h',
    payNowTitle: 'Pay now, by card', payNowHint: 'Your slot is secured instantly',
    payLaterTitle: 'At the venue', payLaterHint: 'Released if you arrive over 10 min late',
    bonoTitle: 'With a session pack', bonoNone: 'You have no active packs',
    bonoBuy: 'Buy', bono: 'Pack',
    confirmFree: 'Confirm booking', payAndBook: 'Pay {amount} and book',
    bookAndPayLater: 'Book and pay at the venue',
    footnote: 'You will get the confirmation by email.',
    sessionsLeft: 'sessions left', creditsLeft: 'credits', with: 'with',
  },
};

function formatMoney(cents: number, currency?: string | null): string {
  const amount = cents / 100;
  const curr = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: curr }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${curr}`;
  }
}

export default function CartReview({
  cart,
  language = 'es',
  hasIdentity = false,
  onRemoveLine,
  onAddAnother,
  onConfirm,
  onEditLine,
  onBuyBundle,
  serverError,
  initialContact,
}: CartReviewProps) {
  const t = L[language] ?? L.es;
  const locale = localeTagFromLang(language);
  const [email, setEmail] = useState(initialContact?.email ?? '');
  const [name, setName] = useState(initialContact?.name ?? '');
  const [phone, setPhone] = useState(initialContact?.phone ?? '');
  const [fieldErrors, setFieldErrors] = useState<GuestContactErrors>({});
  // La ficha de Clerk llega de forma asíncrona y el formulario puede aparecer con el componente ya
  // montado (rechazo del servidor): se rellena lo que esté vacío, sin pisar lo que el cliente escriba.
  const initialName = initialContact?.name;
  const initialEmail = initialContact?.email;
  const initialPhone = initialContact?.phone;
  useEffect(() => {
    if (initialName) setName((v) => v || initialName);
    if (initialEmail) setEmail((v) => v || initialEmail);
    if (initialPhone) setPhone((v) => v || initialPhone);
  }, [initialName, initialEmail, initialPhone]);
  // Bono opt-in por línea (índices de items seleccionados para pagar con bono).
  const [bonoSel, setBonoSel] = useState<Set<number>>(new Set());
  const items = cart.items || [];
  // Modalidades ofrecidas por el carrito (regla 2), calculadas server-side. Lista vacía = el carrito
  // no cobra nada (todo gratis o servicios con requires_payment=false) → se confirma directo sin
  // preguntar. Fallback conservador solo si el backend no envía el campo.
  const allowedModes = cart.allowedPaymentModes ?? (cart.totalAmount > 0 ? ['prepaid'] : []);
  const needsPayment = allowedModes.length > 0;
  const canPrepaid = allowedModes.includes('prepaid');
  const canPostpaid = allowedModes.includes('postpaid');
  // Sin identidad se piden datos en TODO carrito (también gratis): sin ellos la reserva nacía anónima
  // y ni el cliente ni el negocio recibían aviso. Con identidad (Clerk/canal) no se pide nada.
  const needsContact = needsGuestContact(hasIdentity, items.length);
  // Bono: solo se ofrece si el backend reporta cobertura disponible y el carrito permite prepaid
  // (el bono sustituye al pago anticipado). El opt-in es por línea elegible.
  const bonoAvailable = !!cart.bono?.available && canPrepaid;
  const bonoEligibleIndexes = items
    .map((it, idx) => (it.bonoEligible ? idx : -1))
    .filter((idx) => idx >= 0);
  const bonoOfferable = canPrepaid && (bonoAvailable ? bonoEligibleIndexes.length > 0 : !!onBuyBundle);

  // Modalidad elegida. Por defecto la primera realmente disponible, respetando el orden del diseño:
  // pagar ahora → con pack → en el centro.
  const defaultMode: 'prepaid' | 'bono' | 'postpaid' = canPrepaid ? 'prepaid' : 'postpaid';
  const [mode, setMode] = useState<'prepaid' | 'bono' | 'postpaid'>(defaultMode);

  const selectBonoMode = () => {
    if (!bonoAvailable) return; // sin packs activos, la fila solo ofrece "Comprar"
    setMode('bono');
    setBonoSel(new Set(bonoEligibleIndexes.slice(0, cart.bono?.coverableCount ?? bonoEligibleIndexes.length)));
  };

  const effectiveBonoSel = mode === 'bono' ? bonoSel : new Set<number>();
  // Total efectivo (céntimos) descontando las líneas que se pagan con bono.
  const coveredCents = items.reduce(
    (s, it, idx) => s + (effectiveBonoSel.has(idx) ? Math.round((it.price || 0) * 100) : 0),
    0,
  );
  const effectiveTotalCents = Math.max(0, cart.totalAmount - coveredCents);
  // No hay nada que cobrar: carrito gratis O el bono cubre todo lo cobrable.
  const nothingToPay = effectiveTotalCents === 0;
  // "Gratis" solo si el total es realmente 0 (un servicio con precio pero sin cobro muestra su precio).
  const showFreeLabel = effectiveTotalCents === 0 && cart.totalAmount === 0;

  const handleConfirmClick = () => {
    if (items.length === 0) return;
    let contact: { name: string; email?: string; phone?: string } | undefined;
    if (needsContact) {
      const result = validateGuestContact({ name, email, phone });
      if (!result.ok) {
        setFieldErrors(result.errors);
        return;
      }
      contact = result.contact;
    }
    setFieldErrors({});
    if (!needsPayment) {
      onConfirm(contact, undefined, []);
      return;
    }
    // El bono solo aplica al pagar ahora (prepaid): el backend lo consume en esa modalidad.
    const paymentMode: 'prepaid' | 'postpaid' = mode === 'postpaid' ? 'postpaid' : 'prepaid';
    const bonoLineIndexes = mode === 'bono' ? [...bonoSel].sort((a, b) => a - b) : [];
    onConfirm(contact, paymentMode, bonoLineIndexes);
  };

  const ctaLabel = !needsPayment || nothingToPay
    ? t.confirmFree
    : mode === 'postpaid'
      ? t.bookAndPayLater
      : t.payAndBook.replace('{amount}', formatMoney(effectiveTotalCents, cart.currency));

  const bonoBalanceHint =
    cart.bono && cart.bono.bundleSessionsRemaining > 0
      ? `${cart.bono.bundleSessionsRemaining} ${t.sessionsLeft}`
      : cart.bono && cart.bono.creditsBalance > 0
        ? `${cart.bono.creditsBalance} ${t.creditsLeft}`
        : t.bonoNone;

  return (
    <div className="cart-review bk-step bk-review">
      <BookingStepProgress current={3} stepLabel={t.step} title={t.title} />

      {items.length === 0 ? (
        <p className="cart-bottom-meta">{t.empty}</p>
      ) : (
        <div className="bk-review-card">
          {items.map((it, idx) => {
            const durStr = it.durationMinutes
              ? it.durationMinutes >= 60
                ? `${Math.floor(it.durationMinutes / 60)}${t.h}${it.durationMinutes % 60 ? ` ${it.durationMinutes % 60}${t.min}` : ''}`
                : `${it.durationMinutes} ${t.min}`
              : '';
            const dt = it.slotStartTime ? new Date(it.slotStartTime) : null;
            const dateStr = dt
              ? dt.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '')
              : '';
            const timeStr = dt
              ? dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
              : '';
            const metaLine = [dateStr, timeStr, durStr].filter(Boolean).join(' · ');
            const paidWithBono = effectiveBonoSel.has(idx);
            return (
              <div key={`${it.serviceId}-${idx}`} className="bk-review-line">
                <div className="bk-review-line-top">
                  <div className="bk-review-line-text">
                    <span className="bk-review-line-name">{it.serviceName ?? it.serviceId}</span>
                    {metaLine ? <span className="bk-review-line-meta">{metaLine}</span> : null}
                    {it.resourceName ? (
                      <span className="bk-review-line-sub">{`${t.with} ${it.resourceName}`}</span>
                    ) : null}
                  </div>
                  <span className={`bk-review-line-price${paidWithBono ? ' bk-review-line-price--bono' : ''}`}>
                    {paidWithBono ? t.bono : it.price ? formatMoney(Math.round(it.price * 100), it.currency) : t.free}
                  </span>
                </div>
                <div className="bk-review-line-actions">
                  {onEditLine ? (
                    <button type="button" className="bk-link bk-link--accent" onClick={() => onEditLine(idx, it)}>
                      {t.edit}
                    </button>
                  ) : null}
                  <button type="button" className="bk-link" onClick={() => onRemoveLine(idx)}>
                    {t.remove}
                  </button>
                </div>
              </div>
            );
          })}

          <button type="button" className="bk-review-add" onClick={onAddAnother}>
            <Plus size={15} strokeWidth={2.2} aria-hidden />
            {t.addAnother}
          </button>
        </div>
      )}

      <div className="bk-total-row">
        <span>{t.total}</span>
        <strong>{showFreeLabel ? t.free : formatMoney(effectiveTotalCents, cart.currency)}</strong>
      </div>

      {needsPayment ? (
        <>
          <div className="bk-section-label bk-section-label--spaced">
            <span>{t.howToPay}</span>
            <i aria-hidden />
          </div>

          <div className="bk-pay-options" role="radiogroup" aria-label={t.howToPay}>
            {canPrepaid ? (
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'prepaid'}
                className={`bk-pay-option${mode === 'prepaid' ? ' bk-pay-option--on' : ''}`}
                onClick={() => setMode('prepaid')}
              >
                <span className="bk-radio" aria-hidden />
                <span className="bk-pay-option-text">
                  <span className="bk-pay-option-title">{t.payNowTitle}</span>
                  <span className="bk-pay-option-hint">{t.payNowHint}</span>
                </span>
              </button>
            ) : null}

            {bonoOfferable ? (
              <div
                className={`bk-pay-option${mode === 'bono' ? ' bk-pay-option--on' : ''}${bonoAvailable ? '' : ' bk-pay-option--inert'}`}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === 'bono'}
                  disabled={!bonoAvailable}
                  className="bk-pay-option-main"
                  onClick={selectBonoMode}
                >
                  <span className="bk-radio" aria-hidden />
                  <span className="bk-pay-option-text">
                    <span className="bk-pay-option-title">{t.bonoTitle}</span>
                    <span className="bk-pay-option-hint">{bonoBalanceHint}</span>
                  </span>
                </button>
                {onBuyBundle ? (
                  <button type="button" className="bk-link bk-link--accent" onClick={onBuyBundle}>
                    {t.bonoBuy}
                  </button>
                ) : null}
              </div>
            ) : null}

            {canPostpaid ? (
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'postpaid'}
                className={`bk-pay-option${mode === 'postpaid' ? ' bk-pay-option--on' : ''}`}
                onClick={() => setMode('postpaid')}
              >
                <span className="bk-radio" aria-hidden />
                <span className="bk-pay-option-text">
                  <span className="bk-pay-option-title">{t.payLaterTitle}</span>
                  <span className="bk-pay-option-hint">{t.payLaterHint}</span>
                </span>
              </button>
            ) : null}
          </div>

          {cart.onlinePaymentUnavailable ? (
            <p className="bk-pay-unavailable" role="note">{t.onlinePayUnavailable}</p>
          ) : null}
        </>
      ) : null}

      {needsContact ? (
        <div className="cart-contact bk-contact">
          <div className="cart-contact-label">{t.contact}</div>
          <p className="cart-contact-hint">{t.contactHint}</p>
          <input
            type="text"
            name="name"
            autoComplete="name"
            placeholder={t.name}
            aria-label={t.name}
            aria-invalid={!!fieldErrors.name}
            value={name}
            onChange={(e) => { setName(e.target.value); setFieldErrors((f) => ({ ...f, name: undefined })); }}
          />
          {fieldErrors.name ? <div className="cart-contact-error" role="alert">{t.nameRequired}</div> : null}
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t.email}
            aria-label={t.email}
            aria-invalid={!!fieldErrors.email || !!fieldErrors.contact}
            value={email}
            onChange={(e) => { setEmail(e.target.value); setFieldErrors((f) => ({ ...f, email: undefined, contact: undefined })); }}
          />
          {fieldErrors.email ? <div className="cart-contact-error" role="alert">{t.emailInvalid}</div> : null}
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            placeholder={t.phone}
            aria-label={t.phone}
            aria-invalid={!!fieldErrors.phone || !!fieldErrors.contact}
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setFieldErrors((f) => ({ ...f, phone: undefined, contact: undefined })); }}
          />
          {fieldErrors.phone ? <div className="cart-contact-error" role="alert">{t.phoneInvalid}</div> : null}
          {fieldErrors.contact ? <div className="cart-contact-error" role="alert">{t.contactRequired}</div> : null}
        </div>
      ) : null}

      {serverError ? <div className="cart-contact-error" role="alert">{serverError}</div> : null}

      <div className="bk-review-footer">
        <button
          type="button"
          className="btn-primary bk-review-cta"
          disabled={items.length === 0}
          onClick={handleConfirmClick}
        >
          {ctaLabel}
        </button>
        <p className="bk-review-footnote">{t.footnote}</p>
      </div>
    </div>
  );
}
